import { CreatorSetupStatus } from '../enums';
import { DomainError } from '../errors/domain-error';

export type CreatorSetupDraft = {
  memberTopics?: string[] | null;
  memberHeadline?: string | null;
  memberBio?: string | null;
  featuredLinks?: Array<{ label: string; url: string }> | null;
};

export function isPublicCreatorStatus(status: CreatorSetupStatus | string): boolean {
  return status === CreatorSetupStatus.ACTIVE;
}

export function canAccessStudio(status: CreatorSetupStatus | string): boolean {
  return (
    status === CreatorSetupStatus.ACTIVE ||
    status === CreatorSetupStatus.PAST_DUE
  );
}

export function canResumeCheckout(status: CreatorSetupStatus | string): boolean {
  return (
    status === CreatorSetupStatus.INTENT_STARTED ||
    status === CreatorSetupStatus.PROFILE_DRAFT ||
    status === CreatorSetupStatus.PREVIEW_READY ||
    status === CreatorSetupStatus.CHECKOUT_STARTED
  );
}

export function assertCanStartSetup(status: CreatorSetupStatus | string): void {
  if (
    status !== CreatorSetupStatus.NOT_CREATOR &&
    status !== CreatorSetupStatus.INTENT_STARTED &&
    status !== CreatorSetupStatus.PROFILE_DRAFT
  ) {
    throw new DomainError('Creator setup already in progress or completed');
  }
}

export function assertCanSaveDraft(status: CreatorSetupStatus | string): void {
  const allowed: string[] = [
    CreatorSetupStatus.INTENT_STARTED,
    CreatorSetupStatus.PROFILE_DRAFT,
    CreatorSetupStatus.PREVIEW_READY,
    CreatorSetupStatus.CHECKOUT_STARTED,
  ];
  if (!allowed.includes(status)) {
    throw new DomainError('Creator profile cannot be edited in the current state');
  }
}

export function assertPreviewRequirements(draft: CreatorSetupDraft): void {
  const topicCount = (draft.memberTopics ?? []).map((topic) => topic.trim()).filter(Boolean).length;
  const memberHeadline = draft.memberHeadline?.trim();
  const memberBio = draft.memberBio?.trim();

  if (topicCount < 1) {
    throw new DomainError('At least one profile topic is required before preview');
  }
  if (!memberHeadline && !memberBio) {
    throw new DomainError('Profile headline or bio is required before preview');
  }
}

export function assertCanStartCheckout(status: CreatorSetupStatus | string): void {
  if (!canResumeCheckout(status)) {
    throw new DomainError('Creator checkout is not available in the current state');
  }
}

export function assertCanActivate(status: CreatorSetupStatus | string): void {
  if (status !== CreatorSetupStatus.CHECKOUT_STARTED) {
    throw new DomainError('Checkout must be started before activation');
  }
}

export function resolveSetupStatusAfterDraftSave(
  current: CreatorSetupStatus | string,
): CreatorSetupStatus {
  if (
    current === CreatorSetupStatus.INTENT_STARTED ||
    current === CreatorSetupStatus.PROFILE_DRAFT
  ) {
    return CreatorSetupStatus.PROFILE_DRAFT;
  }
  return current as CreatorSetupStatus;
}
